/**
 * How the product works, in the terms a customer needs before paying.
 *
 * One mechanic carries the whole page — rank is the lifetime total paid — so
 * the page states it, shows it moving, and then prices it. Every figure below
 * is derived from `@bidtohire/shared`, not typed in: the arithmetic the page
 * teaches is the arithmetic the payment code performs.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import {
  LISTING_LIFETIME_DAYS,
  MINIMUM_FIRST_BID_CENTS,
  MINIMUM_RAISE_INCREMENT_CENTS,
  PASS_INCREMENT_CENTS,
} from '@bidtohire/shared';

import { PUBLISH_FROM_LABEL } from '../lib/bid-pricing';
import { CHALLENGER, EXAMPLE, LEADER, THIRD } from '../lib/about-example';
import { Money } from '../components/Money';
import { RankingBoard } from '../components/RankingBoard';
import { ArrowRight, MoneyWhereMouthIs } from '../components/ui';

export function AboutPage(): ReactNode {
  return (
    <>
      {/* The rule, stated plainly, with its three prices beside it. --------- */}
      <div className="about-hero">
        <div className="inner">
          <div className="copy">
            <span className="eyebrow">How ranking works</span>
            <h1>Rank is the total paid.</h1>
            <p className="lede">
              Every listing is ordered by the cumulative amount paid on it.
              No relevance score, no promoted tier, no way to move up except to pay more. The
              figure is printed on the row.
            </p>
          </div>

          <div className="card rulecard">
            <span className="eyebrow">The rule, in three lines</span>
            <div className="rule">
              <span>Bid any amount</span>
              <span className="value">
                from <Money cents={MINIMUM_FIRST_BID_CENTS} />
              </span>
            </div>
            <div className="rule">
              <span>Your rank</span>
              <span className="value">the total you have paid</span>
            </div>
            <div className="rule">
              <span>Pass any listing</span>
              <span className="value">
                their total + <Money cents={PASS_INCREMENT_CENTS} />
              </span>
            </div>
            <p className="foot">
              Whole dollars, one-time, cumulative and non-refundable. A listing’s position is its
              lifetime total.
            </p>
          </div>

          <div className="row">
            <Link className="pill pill-primary" to="/post">
              Post a job — {PUBLISH_FROM_LABEL}
              <ArrowRight />
            </Link>
            <Link className="pill pill-quiet" to="/">
              See what #1 costs today
            </Link>
          </div>
        </div>
      </div>

      {/* The mechanic, moving. --------------------------------------------- */}
      <section className="about-sec is-first">
        <div className="inner">
          <div className="about-head">
            <h2>A position changes hands</h2>
            <p>
              In the worked example below, {CHALLENGER.company.name} sits fourth on{' '}
              <Money cents={CHALLENGER.totalCents} /> paid. Passing the leader takes{' '}
              <Money cents={EXAMPLE.firstPlaceTotalCents} /> — and because a listing’s total is
              cumulative, it pays only the difference.
            </p>
          </div>

          <RankingBoard />

          <div className="notegrid">
            <div className="note">
              <div className="title">The price is public</div>
              <p>
                Every row states what its company has paid, and so does the figure that would pass
                it: that listing’s total plus <Money cents={PASS_INCREMENT_CENTS} />. The same
                arithmetic reads every row on the board, first place included.
              </p>
            </div>
            <div className="note">
              <div className="title">You pay the difference</div>
              <p>
                A raise is a top-up. {CHALLENGER.company.name} held{' '}
                <Money cents={CHALLENGER.totalCents} /> and paid <Money cents={EXAMPLE.topUpCents} />
                . A rival still has to beat the <Money cents={EXAMPLE.firstPlaceTotalCents} />{' '}
                total, not the <Money cents={EXAMPLE.topUpCents} />.
              </p>
            </div>
            <div className="note">
              <div className="title">Nothing is taken back</div>
              <p>
                In the example, Northbeam’s <Money cents={LEADER.totalCents} /> stays on its listing. It ranks
                second on that total until it tops up again or someone else passes it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The arithmetic. ---------------------------------------------------- */}
      <section className="about-sec">
        <div className="inner">
          <div className="about-head">
            <h2>What a position costs</h2>
            <p>
              One-time payments in whole dollars, through Stripe. Every figure below is shown on
              the row before you pay it.
            </p>
          </div>

          <div className="pricegrid3">
            <div className="card pricecard">
              <span className="eyebrow">Post</span>
              <div className="equation is-single">
                <Money cents={MINIMUM_FIRST_BID_CENTS} />
              </div>
              <p>
                The minimum first payment, and it comes first. It reserves the listing and buys its
                opening position in one go; the listing goes on the board as soon as its details
                are submitted.
              </p>
            </div>

            <div className="card pricecard">
              <span className="eyebrow">Pass the listing at #3</span>
              <div className="equation">
                <Money cents={THIRD.totalCents} />
                <span className="op" aria-hidden="true">
                  +
                </span>
                <Money cents={PASS_INCREMENT_CENTS} />
                <span className="op" aria-hidden="true">
                  =
                </span>
                <Money className="is-result" cents={EXAMPLE.claimThirdTotalCents} />
              </div>
              <p>
                In the example, {THIRD.company.name} holds third on{' '}
                <Money cents={THIRD.totalCents} /> paid. Sitting above it takes that listing’s total
                plus <Money cents={PASS_INCREMENT_CENTS} /> — the smallest amount that passes it.
                Bid more and you simply land higher.
              </p>
            </div>

            <div className="card pricecard">
              <span className="eyebrow">Take the top spot</span>
              <div className="equation">
                <Money cents={LEADER.totalCents} />
                <span className="op" aria-hidden="true">
                  +
                </span>
                <Money cents={PASS_INCREMENT_CENTS} />
                <span className="op" aria-hidden="true">
                  =
                </span>
                <Money className="is-result" cents={EXAMPLE.firstPlaceTotalCents} />
              </div>
              <p>
                The leader’s total plus <Money cents={PASS_INCREMENT_CENTS} />. First place is
                priced by the same arithmetic as every other row — it carries no premium — and the
                figure moves every time the leader is passed.
              </p>
            </div>
          </div>

          <div className="card is-flat topup">
            <div>
              <span className="eyebrow">A top-up, in full</span>
              <div className="ledger">
                <div className="line">
                  <span>Total you hold</span>
                  <Money cents={CHALLENGER.totalCents} />
                </div>
                <div className="line">
                  <span>Total to pass the leader</span>
                  <Money cents={EXAMPLE.firstPlaceTotalCents} />
                </div>
                <div className="line is-total">
                  <span>You pay today</span>
                  <Money cents={EXAMPLE.topUpCents} />
                </div>
              </div>
            </div>
            <div className="prose-side">
              <p>
                Raising is a top-up. The board remembers your lifetime total, so you pay the
                difference between the total you hold and the total you want. A competitor has to
                beat the <em>total</em>, not the difference — the <Money cents={EXAMPLE.topUpCents} />{' '}
                buys a <Money cents={EXAMPLE.firstPlaceTotalCents} /> position.
              </p>
              <p>
                Equal totals stay in the order they were reached: the listing that got there first
                keeps the higher position.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who the board is for, on both sides. ------------------------------- */}
      <section className="about-sec">
        <div className="inner">
          <div className="card audiences">
            <div>
              <span className="eyebrow">If you are looking for work</span>
              <div className="tagline">They’re bidding on you.</div>
              <div className="facts">
                <p>Rows are ordered by what the company paid. Nothing else moves them.</p>
                <p>A salary range is required on every listing, and shown on every row.</p>
                <p>Applying opens the company’s own posting. We never take applications.</p>
                <p>No account, no résumé, no profile — browsing needs nothing at all.</p>
              </div>
              <Link className="go" to="/">
                Browse the leaderboard
                <ArrowRight colour="currentColor" />
              </Link>
            </div>

            <div className="divider" aria-hidden="true" />

            <div>
              <span className="eyebrow">If you are hiring</span>
              <div className="tagline">
                <MoneyWhereMouthIs />
              </div>
              <div className="facts">
                <p>
                  <Money cents={MINIMUM_FIRST_BID_CENTS} /> reserves a listing and buys its
                  position in the same payment.
                </p>
                <p>
                  Raise whenever you like; every later raise adds at least{' '}
                  <Money cents={MINIMUM_RAISE_INCREMENT_CENTS} />.
                </p>
                <p>One payment counts on all three boards: global, category, specialism.</p>
                <p>
                  Terms run {LISTING_LIFETIME_DAYS} days and renew free, carrying the total and the
                  position.
                </p>
              </div>
              <Link className="go" to="/post">
                Post a job — {PUBLISH_FROM_LABEL}
                <ArrowRight colour="currentColor" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The order of events, once. ----------------------------------------- */}
      <section className="about-sec">
        <div className="inner">
          <div className="about-head">
            <h2>The life of a listing</h2>
            <p>The money comes first, and nothing after it is hidden.</p>
          </div>

          <div className="lifecycle">
            <div className="phase">
              <span className="n">01</span>
              <div className="title">Reserve it {PUBLISH_FROM_LABEL}</div>
              <p>
                Choose what to spend and pay. That payment buys the listing’s opening position on
                all three of the boards it will appear on, and holds it until the details arrive.
              </p>
            </div>
            <div className="phase">
              <span className="n">02</span>
              <div className="title">Add the posting</div>
              <p>
                Paste the address of the job posting and we read the details off it — the title,
                the description, the salary. Check every field, submit, and the listing publishes
                immediately.
              </p>
            </div>
            <div className="phase">
              <span className="n">03</span>
              <div className="title">Raise when you want</div>
              <p>
                Top up to any total. The board reorders when the payment completes, not before. We
                email you when a listing loses the top of a board.
              </p>
            </div>
            <div className="phase">
              <span className="n">04</span>
              <div className="title">Renew free at {LISTING_LIFETIME_DAYS} days</div>
              <p>
                Terms run {LISTING_LIFETIME_DAYS} days. Renewal is free from the dashboard, and the
                total paid and the position carry over untouched.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The questions the mechanic raises. ---------------------------------- */}
      <section className="about-sec">
        <div className="inner">
          <div className="about-head">
            <h2>Common questions</h2>
          </div>

          <div className="faq">
            <div className="qa">
              <div className="q">Can I outrank someone without paying more than they have?</div>
              <p className="a">
                No. A position is the lifetime total paid, so passing a listing means passing its
                total. Equal totals stay in the order they were reached — the listing that got
                there first keeps the higher position.
              </p>
            </div>
            <div className="qa">
              <div className="q">What happens to my money when I’m passed?</div>
              <p className="a">
                It stays on your listing. Your total never goes down, so you keep every position
                below the one that passed you, and a later top-up starts from the total you already
                hold. Payments are non-refundable.
              </p>
            </div>
            <div className="qa">
              <div className="q">Is the amount public?</div>
              <p className="a">
                Yes. Every row shows the total its company has paid, to candidates and competitors
                alike, along with the price of claiming that position.
              </p>
            </div>
            <div className="qa">
              <div className="q">Do candidates need an account?</div>
              <p className="a">
                No. Browsing needs no account and there is no application form. Applying opens the
                company’s own posting, on the company’s own site.
              </p>
            </div>
            <div className="qa">
              <div className="q">Does one payment count on every board?</div>
              <p className="a">
                Yes. A listing appears on the global board, its category board and its specialism
                board, and a single payment counts on all three. A narrower board is never more expensive to
                lead.
              </p>
            </div>
            <div className="qa">
              <div className="q">A payment cleared and nothing moved.</div>
              <p className="a">
                A position is claimed when the payment completes, not before. If a payment
                completes and no position follows, contact us for a refund of that payment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Close. -------------------------------------------------------------- */}
      <section className="about-sec is-close">
        <div className="inner">
          <div className="card swagger">
            <div className="line">
              <Money cents={MINIMUM_FIRST_BID_CENTS} /> puts you on the board.
            </div>
            <p>
              Pay first and the position is yours. Then paste your posting, we fill in the
              details, and it goes live.
            </p>
            <div className="row" style={{ marginTop: 20 }}>
              <Link className="pill pill-primary" to="/post">
                Post a job — {PUBLISH_FROM_LABEL}
                <ArrowRight />
              </Link>
              <Link className="pill pill-quiet" to="/for-employers">
                For employers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
